<template>
  <div class="api-settings-root">
    <div class="api-settings-header">
      <div class="api-settings-header-text">
        <div class="settings-section-title">Public API</div>
        <div class="settings-section-desc">
          Generate API key pairs to allow external applications to read and write spreadsheet records.
          Each key can be restricted to specific CRUD operations and spreadsheets. Pages are not available through API keys.
        </div>
      </div>
    </div>

    <!-- Key list section -->
    <div class="api-section">
      <div class="api-section-heading-row">
        <div>
          <div class="api-section-heading">API Keys</div>
          <div class="api-section-hint">Each key pair grants scoped access to spreadsheet records in this app.</div>
        </div>
        <button class="api-add-btn" @click="showCreateModal = true">
          <font-awesome-icon icon="plus" /> new API key
        </button>
      </div>

      <div v-if="keys.length" class="api-key-list">
        <div
          v-for="(key, index) in keys"
          :key="key._id"
          class="api-key-card"
        >
          <div class="api-key-header">
            <button type="button" class="api-key-summary" :aria-expanded="expanded[index] ? 'true' : 'false'" @click="toggleExpand(index)">
              <font-awesome-icon :icon="expanded[index] ? 'chevron-down' : 'chevron-right'" class="api-expand-icon" />
              <span class="api-key-name">{{ key.name }}</span>
              <span class="api-key-status-badge" :class="key.active ? 'status-active' : 'status-inactive'">
                {{ key.active ? 'active' : 'inactive' }}
              </span>
              <span class="api-key-id-badge">{{ key.apiKey.substring(0, 16) }}...</span>
            </button>
            <div class="api-key-header-actions">
              <span v-if="key.lastUsedAt" class="api-key-last-used">last used {{ formatRelative(key.lastUsedAt) }}</span>
              <button class="api-remove-btn" @click.stop="deleteKey(key)" title="Delete key">
                <font-awesome-icon icon="trash" />
              </button>
            </div>
          </div>

          <div v-if="expanded[index]" class="api-key-details">
            <!-- Credentials -->
            <div class="api-detail-section">
              <div class="api-detail-title">Credentials</div>

              <div class="api-field-row">
                <label class="api-label">Name</label>
                <div class="api-input-with-btn">
                  <input class="api-input" v-model="key.name" @blur="updateKey(key)" />
                </div>
              </div>

              <div class="api-field-row">
                <label class="api-label">API Key</label>
                <div class="api-input-with-btn">
                  <input class="api-input" :value="key.apiKey" readonly />
                  <button class="api-copy-btn" @click="copyToClipboard(key.apiKey)" title="Copy">
                    <font-awesome-icon icon="copy" />
                  </button>
                </div>
              </div>

              <div class="api-field-row">
                <label class="api-label">API Secret</label>
                <div class="api-input-with-btn">
                  <input class="api-input" :value="key._revealedSecret || '••••••••••••••••'" readonly />
                  <button class="api-gen-btn" @click="regenerateSecret(key)" title="Regenerate secret">
                    <font-awesome-icon icon="sync-alt" />
                  </button>
                </div>
                <div class="api-field-hint" v-if="key._revealedSecret">
                  Copy this secret now — it will not be shown again.
                </div>
              </div>

              <div class="api-field-row">
                <label class="api-label">Status</label>
                <label class="api-toggle-row">
                  <input type="checkbox" v-model="key.active" class="api-checkbox" @change="updateKey(key)" />
                  <span class="api-toggle-label">{{ key.active ? 'Active' : 'Disabled' }}</span>
                </label>
              </div>
            </div>

            <!-- Global Permissions -->
            <div class="api-detail-section">
              <div class="api-detail-title">Global Permissions</div>
              <div class="api-detail-hint">Default CRUD permissions that apply to all spreadsheets unless overridden below.</div>
              <div class="api-perm-grid">
                <label class="api-perm-item" v-for="op in crudOps" :key="op">
                  <input type="checkbox" v-model="key.permissions.globalCrud[op]" class="api-checkbox" @change="updateKey(key)" />
                  <span>{{ op }}</span>
                </label>
              </div>
            </div>

            <!-- Spreadsheet-level permissions -->
            <div class="api-detail-section">
              <div class="api-detail-title">Spreadsheet Permissions</div>
              <div class="api-detail-hint">Override global permissions for specific spreadsheets. If set, these take priority.</div>

              <div class="api-resource-perm" v-for="ss in spreadsheets" :key="ss.uniqueTitle">
                <button type="button" class="api-resource-header" :aria-expanded="key.permissions.spreadsheets[ss.uniqueTitle] ? 'true' : 'false'" @click="toggleSpreadsheetPerm(key, ss.uniqueTitle)">
                  <font-awesome-icon
                    :icon="key.permissions.spreadsheets[ss.uniqueTitle] ? 'chevron-down' : 'chevron-right'"
                    class="api-expand-icon"
                  />
                  <span class="api-resource-name">{{ ss.title || ss.uniqueTitle }}</span>
                  <span v-if="key.permissions.spreadsheets[ss.uniqueTitle]" class="api-override-badge">custom</span>
                </button>
                <div v-if="key.permissions.spreadsheets[ss.uniqueTitle]" class="api-resource-perms">
                  <div class="api-perm-grid">
                    <label class="api-perm-item" v-for="op in crudOps" :key="op">
                      <input
                        type="checkbox"
                        v-model="key.permissions.spreadsheets[ss.uniqueTitle][op]"
                        class="api-checkbox"
                        @change="updateKey(key)"
                      />
                      <span>{{ op }}</span>
                    </label>
                  </div>
                  <button class="api-remove-override-btn" @click="removeSpreadsheetPerm(key, ss.uniqueTitle)">
                    remove override
                  </button>
                </div>
              </div>
            </div>

            <!-- Endpoint reference -->
            <div class="api-detail-section">
              <div class="api-detail-title">Endpoint Reference</div>
              <div class="api-endpoints">
                <div class="api-endpoint-row">
                  <span class="api-endpoint-label">List Spreadsheets:</span>
                  <code class="api-endpoint-url">GET {{ baseUrl }}/api/v1/spreadsheets</code>
                </div>
                <div class="api-endpoint-row">
                  <span class="api-endpoint-label">Read Records:</span>
                  <code class="api-endpoint-url">GET {{ baseUrl }}/api/v1/spreadsheets/:id/records</code>
                </div>
                <div class="api-endpoint-row">
                  <span class="api-endpoint-label">Get Record:</span>
                  <code class="api-endpoint-url">GET {{ baseUrl }}/api/v1/spreadsheets/:id/records/:recordId</code>
                </div>
                <div class="api-endpoint-row">
                  <span class="api-endpoint-label">Create Records:</span>
                  <code class="api-endpoint-url">POST {{ baseUrl }}/api/v1/spreadsheets/:id/records</code>
                </div>
                <div class="api-endpoint-row">
                  <span class="api-endpoint-label">Update Record:</span>
                  <code class="api-endpoint-url">PUT {{ baseUrl }}/api/v1/spreadsheets/:id/records/:recordId</code>
                </div>
                <div class="api-endpoint-row">
                  <span class="api-endpoint-label">Delete Records:</span>
                  <code class="api-endpoint-url">DELETE {{ baseUrl }}/api/v1/spreadsheets/:id/records</code>
                </div>
                <div class="api-endpoint-row">
                  <span class="api-endpoint-label">Bulk Update:</span>
                  <code class="api-endpoint-url">PUT {{ baseUrl }}/api/v1/spreadsheets/:id/records/bulk</code>
                </div>
                <div class="api-endpoint-row">
                  <span class="api-endpoint-label">Search Records:</span>
                  <code class="api-endpoint-url">GET {{ baseUrl }}/api/v1/spreadsheets/:id/records/search</code>
                </div>
                <div class="api-endpoint-row">
                  <span class="api-endpoint-label">Link Record:</span>
                  <code class="api-endpoint-url">POST {{ baseUrl }}/api/v1/spreadsheets/:id/records/:recordId/links/:column</code>
                </div>
                <div class="api-endpoint-row">
                  <span class="api-endpoint-label">Unlink Record:</span>
                  <code class="api-endpoint-url">DELETE {{ baseUrl }}/api/v1/spreadsheets/:id/records/:recordId/links/:column/:targetId</code>
                </div>
                </div>
              <div class="api-auth-hint">
                Include <code>X-API-Key</code> and <code>X-API-Secret</code> headers with every request.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="api-key-empty">
        No API keys created yet. Click <strong>+ new API key</strong> to generate your first key pair.
      </div>
    </div>

    <!-- Create modal -->
    <div v-if="showCreateModal" class="api-modal-overlay" @click.self="showCreateModal = false">
      <div class="api-modal">
        <div class="api-modal-title">Create API Key <modal-help topic="settings-api" label="Help for this screen" /></div>
        <div class="api-field-row">
          <label class="api-label">Name</label>
          <input class="api-input" v-model="newKeyName" placeholder="e.g. Mobile App, Webhook, Data Import" ref="newKeyInput" @keydown.enter="createKey" />
        </div>
        <div class="api-modal-actions">
          <button class="api-modal-cancel" @click="showCreateModal = false">cancel</button>
          <button class="api-modal-create" @click="createKey" :disabled="!newKeyName.trim() || creating">
            {{ creating ? 'creating...' : 'create key' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Secret reveal modal -->
    <div v-if="revealedKey" class="api-modal-overlay" @click.self="dismissReveal">
      <div class="api-modal">
        <div class="api-modal-title">API Key Created <modal-help topic="settings-api" label="Help for this screen" /></div>
        <div class="api-modal-warning">
          Copy the API secret now. It will not be displayed again.
        </div>
        <div class="api-field-row">
          <label class="api-label">API Key</label>
          <div class="api-input-with-btn">
            <input class="api-input" :value="revealedKey.apiKey" readonly />
            <button class="api-copy-btn" @click="copyToClipboard(revealedKey.apiKey)" title="Copy">
              <font-awesome-icon icon="copy" />
            </button>
          </div>
        </div>
        <div class="api-field-row">
          <label class="api-label">API Secret</label>
          <div class="api-input-with-btn">
            <input class="api-input api-secret-reveal" :value="revealedKey.apiSecret" readonly />
            <button class="api-copy-btn" @click="copyToClipboard(revealedKey.apiSecret)" title="Copy">
              <font-awesome-icon icon="copy" />
            </button>
          </div>
        </div>
        <div class="api-modal-actions">
          <button class="api-modal-create" @click="dismissReveal">I've copied the secret</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import axios from 'axios'
import { onDesignerChange, raiseDesignerWarning } from '../../lib/designer-sync.js'

export default {
  name: 'SettingsApi',
  props: ['application'],
  data () {
    return {
      keys: [],
      spreadsheets: [],
      pages: [],
      expanded: [],
      showCreateModal: false,
      newKeyName: '',
      creating: false,
      revealedKey: null,
      updateTimer: null,
      baseUrl: VUE_APP_SERVER_URL || VUE_APP_PROXY_SERVER_URL || 'http://localhost:9998',
      crudOps: ['create', 'read', 'update', 'delete']
    }
  },
  computed: {
    viewCapabilities () {
      return {
        table:    { create: false, read: true, update: true, delete: true },
        cards:    { create: false, read: true, update: false, delete: false },
        card:     { create: false, read: true, update: false, delete: false },
        form:     { create: true,  read: false, update: true,  delete: false },
        login:    { create: true,  read: false, update: false, delete: false },
        carousel: { create: false, read: true, update: false, delete: false },
        chart:    { create: false, read: true, update: false, delete: false }
      }
    },
    pagesWithViews () {
      const caps = this.viewCapabilities

      function extractViews (components) {
        const views = []
        if (!components) return views
        for (const comp of components) {
          if (comp.uniqueTitle && caps[comp.type]) {
            views.push({
              uniqueTitle: comp.uniqueTitle,
              title: comp.title || comp.uniqueTitle,
              type: comp.type,
              spreadSheet: comp.spreadSheet || null,
              capabilities: caps[comp.type]
            })
          }
          if (comp.components) {
            views.push(...extractViews(comp.components))
          }
        }
        return views
      }

      return this.pages.map(pg => ({
        ...pg,
        views: extractViews(pg.components)
      }))
    }
  },
  async mounted () {
    await this.loadAll()
    this._stopDesignerSync = onDesignerChange((change) => this.applyDesignerChange(change))
  },
  beforeUnmount () {
    if (this._stopDesignerSync) this._stopDesignerSync()
  },
  watch: {
    async application () {
      await this.loadAll()
    },
    showCreateModal (val) {
      if (val) {
        this.newKeyName = ''
        this.$nextTick(() => {
          if (this.$refs.newKeyInput) this.$refs.newKeyInput.focus()
        })
      }
    }
  },
  methods: {
    async applyDesignerChange (change) {
      const keys = change && change.keys || []
      if (!keys.includes('api-keys') && !keys.includes('application')) return
      if (this.showCreateModal || this.revealedKey) {
        raiseDesignerWarning(change, 'API keys')
        return
      }
      await this.loadKeys()
    },
    async loadAll () {
      await Promise.all([this.loadKeys(), this.loadSpreadsheets(), this.loadPages()])
    },

    async loadKeys () {
      try {
        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/api-keys`, {
          headers: { 'application-unique-title': this.application }
        })
        this.keys = (res.data || []).map(k => ({
          ...k,
          permissions: k.permissions || { globalCrud: { create: true, read: true, update: true, delete: true }, spreadsheets: {}, pages: {} },
          _revealedSecret: null
        }))
        this.expanded = this.keys.map(() => false)
      } catch (err) {
        console.log('failed to load API keys', err)
      }
    },

    async loadSpreadsheets () {
      try {
        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/`, {
          headers: { 'application-unique-title': this.application }
        })
        this.spreadsheets = res.data || []
      } catch (err) {
        console.log('failed to load spreadsheets', err)
      }
    },

    async loadPages () {
      try {
        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/pages/`, {
          headers: { 'application-unique-title': this.application }
        })
        this.pages = res.data || []
      } catch (err) {
        console.log('failed to load pages', err)
      }
    },

    async createKey () {
      if (!this.newKeyName.trim() || this.creating) return

      this.creating = true

      try {
        const res = await axios.post(`${VUE_APP_PROXY_SERVER_URL}/api-keys`, {
          name: this.newKeyName
        }, {
          headers: { 'application-unique-title': this.application }
        })

        this.revealedKey = res.data
        this.showCreateModal = false
        await this.loadKeys()

        const idx = this.keys.findIndex(k => k._id === res.data._id)
        if (idx >= 0) {
          this.expanded[idx] = true
          this.keys[idx]._revealedSecret = res.data.apiSecret
          this.expanded = [...this.expanded]
        }
      } catch (err) {
        console.log('failed to create API key', err)
        alert('Failed to create API key')
      } finally {
        this.creating = false
      }
    },

    async updateKey (key) {
      if (this.updateTimer) clearTimeout(this.updateTimer)

      this.updateTimer = setTimeout(async () => {
        try {
          await axios.put(`${VUE_APP_PROXY_SERVER_URL}/api-keys/${key._id}`, {
            name: key.name,
            permissions: key.permissions,
            active: key.active
          }, {
            headers: { 'application-unique-title': this.application }
          })
        } catch (err) {
          console.log('failed to update API key', err)
        }
      }, 400)
    },

    async deleteKey (key) {
      if (!confirm(`Delete API key "${key.name}"? This will immediately revoke all access.`)) return

      try {
        await axios.delete(`${VUE_APP_PROXY_SERVER_URL}/api-keys/${key._id}`, {
          headers: { 'application-unique-title': this.application }
        })
        await this.loadKeys()
      } catch (err) {
        console.log('failed to delete API key', err)
        alert('Failed to delete API key')
      }
    },

    async regenerateSecret (key) {
      if (!confirm('Regenerate secret? Any application using the current secret will stop working.')) return

      try {
        const res = await axios.post(`${VUE_APP_PROXY_SERVER_URL}/api-keys/${key._id}/regenerate-secret`, {}, {
          headers: { 'application-unique-title': this.application }
        })
        key._revealedSecret = res.data.apiSecret
        this.revealedKey = { apiKey: key.apiKey, apiSecret: res.data.apiSecret }
      } catch (err) {
        console.log('failed to regenerate secret', err)
        alert('Failed to regenerate secret')
      }
    },

    toggleExpand (index) {
      this.expanded[index] = !this.expanded[index]
      this.expanded = [...this.expanded]
    },

    toggleSpreadsheetPerm (key, uniqueTitle) {
      if (key.permissions.spreadsheets[uniqueTitle]) {
        delete key.permissions.spreadsheets[uniqueTitle]
      } else {
        key.permissions.spreadsheets[uniqueTitle] = {
          create: key.permissions.globalCrud.create,
          read: key.permissions.globalCrud.read,
          update: key.permissions.globalCrud.update,
          delete: key.permissions.globalCrud.delete
        }
      }
      key.permissions = { ...key.permissions }
      this.updateKey(key)
    },

    removeSpreadsheetPerm (key, uniqueTitle) {
      delete key.permissions.spreadsheets[uniqueTitle]
      key.permissions = { ...key.permissions }
      this.updateKey(key)
    },

    togglePagePerm (key, uniqueTitle) {
      if (key.permissions.pages[uniqueTitle]) {
        delete key.permissions.pages[uniqueTitle]
      } else {
        key.permissions.pages[uniqueTitle] = {
          create: key.permissions.globalCrud.create,
          read: key.permissions.globalCrud.read,
          update: key.permissions.globalCrud.update,
          delete: key.permissions.globalCrud.delete,
          views: {}
        }
      }
      key.permissions = { ...key.permissions }
      this.updateKey(key)
    },

    removePagePerm (key, uniqueTitle) {
      delete key.permissions.pages[uniqueTitle]
      key.permissions = { ...key.permissions }
      this.updateKey(key)
    },

    getViewPerms (key, pageUniqueTitle, viewUniqueTitle) {
      const pagePerm = key.permissions.pages[pageUniqueTitle]
      if (!pagePerm || !pagePerm.views) return null
      return pagePerm.views[viewUniqueTitle] || null
    },

    toggleViewPerm (key, pageUniqueTitle, viewUniqueTitle) {
      const pagePerm = key.permissions.pages[pageUniqueTitle]
      if (!pagePerm) return

      if (!pagePerm.views) pagePerm.views = {}

      if (pagePerm.views[viewUniqueTitle]) {
        delete pagePerm.views[viewUniqueTitle]
      } else {
        const page = this.pagesWithViews.find(p => p.uniqueTitle === pageUniqueTitle)
        const view = page && page.views.find(v => v.uniqueTitle === viewUniqueTitle)
        const caps = view ? view.capabilities : {}

        const viewPerms = {}
        for (const op of this.crudOps) {
          if (caps[op]) viewPerms[op] = pagePerm[op] || false
        }
        pagePerm.views[viewUniqueTitle] = viewPerms
      }
      key.permissions = { ...key.permissions }
      this.updateKey(key)
    },

    removeViewPerm (key, pageUniqueTitle, viewUniqueTitle) {
      const pagePerm = key.permissions.pages[pageUniqueTitle]
      if (!pagePerm || !pagePerm.views) return
      delete pagePerm.views[viewUniqueTitle]
      key.permissions = { ...key.permissions }
      this.updateKey(key)
    },

    dismissReveal () {
      this.revealedKey = null
    },

    copyToClipboard (text) {
      navigator.clipboard.writeText(text).catch(() => {
        const el = document.createElement('textarea')
        el.value = text
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
      })
    },

    formatRelative (isoDate) {
      if (!isoDate) return 'never'
      const d = new Date(isoDate)
      const now = new Date()
      const diffMs = now - d
      const diffMin = Math.floor(diffMs / 60000)
      if (diffMin < 1) return 'just now'
      if (diffMin < 60) return `${diffMin}m ago`
      const diffHours = Math.floor(diffMin / 60)
      if (diffHours < 24) return `${diffHours}h ago`
      const diffDays = Math.floor(diffHours / 24)
      if (diffDays < 30) return `${diffDays}d ago`
      return d.toLocaleDateString()
    }
  }
}
</script>

<style scoped>

.api-settings-root {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 102px);
  overflow-y: auto;
  padding: 16px 24px;
}

.api-settings-header {
  margin-bottom: 16px;
}

.settings-section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #e8e8e6;
  margin-bottom: 2px;
}

.settings-section-desc {
  font-size: 0.75rem;
  color: #707070;
  max-width: 600px;
}

/* ── Sections ── */
.api-section {
  margin-bottom: 20px;
}

.api-section-heading {
  font-size: 0.82rem;
  font-weight: 600;
  color: #c0c0be;
  margin-bottom: 2px;
}

.api-section-heading-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 10px;
}

.api-section-hint {
  font-size: 0.72rem;
  color: #a8a8a4;
  margin-bottom: 10px;
}

.api-add-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 5px;
  border: 1px solid #3a3a37;
  background: transparent;
  color: #a0a09e;
  font-size: 0.78rem;
  cursor: pointer;
  transition: border-color 0.12s, color 0.12s, background 0.12s;
  flex-shrink: 0;
}

.api-add-btn:hover {
  border-color: #9ad12b;
  color: #e8e8e6;
  background: rgba(154, 209, 43, 0.06);
}

/* ── Key list ── */
.api-key-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.api-key-card {
  border: 1px solid #2a2a27;
  border-radius: 6px;
  background: #141412;
  overflow: hidden;
}

.api-key-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  transition: background 0.1s;
}

.api-key-header:hover {
  background: #1a1a17;
}

.api-key-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  padding: 0;
  cursor: pointer;
}

.api-expand-icon {
  font-size: 0.65rem;
  color: #a8a8a4;
  width: 10px;
}

.api-key-name {
  font-size: 0.82rem;
  color: #c0c0be;
}

.api-key-status-badge {
  font-size: 0.58rem;
  padding: 1px 5px;
  border-radius: 3px;
}

.status-active {
  color: #6cc644;
  background: rgba(108, 198, 68, 0.1);
}

.status-inactive {
  color: #e05050;
  background: rgba(224, 80, 80, 0.1);
}

.api-key-id-badge {
  font-size: 0.62rem;
  color: #a8a8a4;
  background: #1a1a17;
  border: 1px solid #2a2a27;
  padding: 1px 6px;
  border-radius: 3px;
  font-family: monospace;
}

.api-key-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.api-key-last-used {
  font-size: 0.68rem;
  color: #a8a8a4;
}

.api-remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 4px;
  border: 1px solid transparent;
  background: transparent;
  color: #a8a8a4;
  font-size: 0.7rem;
  cursor: pointer;
  transition: color 0.12s, background 0.12s, border-color 0.12s;
}

.api-remove-btn:hover {
  color: #e05050;
  background: rgba(224, 80, 80, 0.08);
  border-color: rgba(224, 80, 80, 0.2);
}

.api-key-empty {
  text-align: center;
  padding: 24px 20px;
  color: #a8a8a4;
  font-size: 0.8rem;
}

.api-key-empty strong {
  color: #707070;
}

/* ── Details panel ── */
.api-key-details {
  border-top: 1px solid #2a2a27;
  padding: 14px;
}

.api-detail-section {
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid #1e1e1b;
}

.api-detail-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.api-detail-title {
  font-size: 0.78rem;
  font-weight: 600;
  color: #a0a09e;
  margin-bottom: 6px;
}

.api-detail-hint {
  font-size: 0.7rem;
  color: #a8a8a4;
  margin-bottom: 8px;
}

/* ── Fields ── */
.api-field-row {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 8px;
}

.api-label {
  font-size: 0.72rem;
  color: #707070;
  font-weight: 500;
}

.api-field-hint {
  font-size: 0.68rem;
  color: #e8a020;
  margin-top: 2px;
}

.api-input {
  width: 100%;
  max-width: 500px;
  padding: 6px 10px;
  border-radius: 4px;
  border: 1px solid #2a2a27;
  background: #0f0f0d;
  color: #e8e8e6;
  font-size: 0.8rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.12s;
}

.api-input:focus {
  border-color: #9ad12b;
}

.api-input::placeholder {
  color: #3a3a37;
}

.api-input[readonly] {
  color: #909090;
  cursor: default;
  font-family: monospace;
  font-size: 0.75rem;
}

.api-secret-reveal {
  color: #9ad12b !important;
}

.api-input-with-btn {
  display: flex;
  gap: 4px;
  max-width: 500px;
}

.api-input-with-btn .api-input {
  flex: 1;
  max-width: none;
}

.api-copy-btn,
.api-gen-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  border-radius: 4px;
  border: 1px solid #2a2a27;
  background: transparent;
  color: #a8a8a4;
  font-size: 0.7rem;
  cursor: pointer;
  transition: color 0.12s, border-color 0.12s;
  flex-shrink: 0;
}

.api-copy-btn:hover,
.api-gen-btn:hover {
  color: #9ad12b;
  border-color: #9ad12b;
}

/* ── Toggle / checkbox ── */
.api-toggle-row {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.api-checkbox {
  accent-color: #9ad12b;
  width: 16px;
  height: 16px;
}

.api-toggle-label {
  font-size: 0.82rem;
  color: #c0c0be;
  font-weight: 500;
}

/* ── Permissions grid ── */
.api-perm-grid {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.api-perm-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  color: #a0a09e;
  cursor: pointer;
}

/* ── Resource-level permissions ── */
.api-resource-perm {
  margin-bottom: 4px;
  border: 1px solid #1e1e1b;
  border-radius: 4px;
  overflow: hidden;
}

.api-resource-header {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 10px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 0.1s;
}

.api-resource-header:hover {
  background: #1a1a17;
}

.api-resource-name {
  font-size: 0.78rem;
  color: #a0a09e;
}

.api-override-badge {
  font-size: 0.58rem;
  color: #e8a020;
  background: rgba(232, 160, 32, 0.1);
  padding: 1px 5px;
  border-radius: 3px;
}

.api-resource-perms {
  padding: 8px 10px 8px 28px;
  border-top: 1px solid #1e1e1b;
  display: flex;
  align-items: center;
  gap: 16px;
}

.api-remove-override-btn {
  background: transparent;
  border: none;
  color: #a8a8a4;
  font-size: 0.68rem;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 3px;
  transition: color 0.12s;
}

.api-remove-override-btn:hover {
  color: #e05050;
}

/* ── Page perms block (page-level + views) ── */
.api-resource-perms-block {
  border-top: 1px solid #1e1e1b;
  padding: 8px 10px;
}

.api-resource-perms-block > .api-resource-perms {
  padding-left: 18px;
  padding-bottom: 8px;
}

/* ── Views within a page ── */
.api-views-section {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #1e1e1b;
  padding-left: 18px;
}

.api-views-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #a8a8a4;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
}

.api-view-perm {
  margin-bottom: 3px;
  border: 1px solid #1a1a17;
  border-radius: 4px;
  overflow: hidden;
}

.api-view-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  cursor: pointer;
  transition: background 0.1s;
}

.api-view-header:hover {
  background: #1a1a17;
}

.api-view-name {
  font-size: 0.75rem;
  color: #909090;
}

.api-view-type-badge {
  font-size: 0.58rem;
  color: #707070;
  background: #1a1a17;
  border: 1px solid #2a2a27;
  padding: 1px 5px;
  border-radius: 3px;
}

.api-view-ss-badge {
  font-size: 0.58rem;
  color: #4a9eff;
  background: rgba(74, 158, 255, 0.08);
  border: 1px solid rgba(74, 158, 255, 0.15);
  padding: 1px 5px;
  border-radius: 3px;
  font-family: monospace;
}

/* ── Endpoints ── */
.api-endpoints {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
}

.api-endpoint-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.api-endpoint-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #707070;
  min-width: 130px;
}

.api-endpoint-url {
  font-size: 0.72rem;
  color: #9ad12b;
  background: #0f0f0d;
  padding: 3px 8px;
  border-radius: 3px;
  border: 1px solid #2a2a27;
  word-break: break-all;
}

.api-auth-hint {
  font-size: 0.7rem;
  color: #a8a8a4;
}

.api-auth-hint code {
  color: #9ad12b;
  font-size: 0.7rem;
}

/* ── Modal ── */
.api-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.api-modal {
  background: #1a1a17;
  border: 1px solid #2a2a27;
  border-radius: 8px;
  padding: 24px;
  width: 440px;
  max-width: 90vw;
}

.api-modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #e8e8e6;
  margin-bottom: 14px;
}

.api-modal-warning {
  font-size: 0.78rem;
  color: #e8a020;
  background: rgba(232, 160, 32, 0.08);
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 14px;
}

.api-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}

.api-modal-cancel {
  padding: 6px 16px;
  border-radius: 4px;
  border: 1px solid #2a2a27;
  background: transparent;
  color: #707070;
  font-size: 0.8rem;
  cursor: pointer;
  transition: color 0.12s, border-color 0.12s;
}

.api-modal-cancel:hover {
  color: #a0a09e;
  border-color: #3a3a37;
}

.api-modal-create {
  padding: 6px 16px;
  border-radius: 4px;
  border: none;
  background: #9ad12b;
  color: #0f0f0d;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s;
}

.api-modal-create:hover {
  background: #b0e840;
}

.api-modal-create:disabled {
  opacity: 0.4;
  cursor: default;
}
</style>
